/**
 * prettyFoam.js - A simple formatter for OpenFOAM dictionary files
 * 
 * This formatter:
 * - Aligns key-value pairs
 * - Properly indents nested structures
 * - Formats lists and arrays consistently
 * - Preserves comments (when possible)
 */

/**
 * Format an OpenFOAM AST into readable text
 * @param {Object} ast - The OpenFOAM AST to format
 * @returns {string} Formatted OpenFOAM text
 */
export default function prettierFoam(ast) {
  if (!ast) return '';
  
  // Start with FoamFile header if present
  let result = '';
  const foamFileEntry = ast.entries.find(entry => entry.key === 'FoamFile');
  
  if (foamFileEntry) {
    result += 'FoamFile\n{\n';
    // Format FoamFile dictionary entries with proper indentation
    if (typeof foamFileEntry.value === 'object' && foamFileEntry.value.entries) {
      const foamFileEntries = foamFileEntry.value.entries;
      const maxKeyLength = Math.max(...foamFileEntries.map(e => e.key.length));
      
      foamFileEntries.forEach(entry => {
        const padding = ' '.repeat(maxKeyLength - entry.key.length + 4);
        result += `    ${entry.key}${padding}${formatValue(entry.value)};\n`;
      });
    }
    result += '}\n\n';
  }
  
  // Format remaining entries
  const regularEntries = ast.entries.filter(entry => entry.key !== 'FoamFile');
  if (regularEntries.length > 0) {
    const maxKeyLength = Math.max(...regularEntries.map(e => e.key?.length || 0));
    
    regularEntries.forEach(entry => {
      // Handle substitution statements specially
      if (entry.type === 'substitution') {
        const variableName = entry.variableName || entry.value?.replace(/^\$/, '') || 'var';
        result += `    $${variableName};\n`;
      } else {
        const entryKey = entry.key || 'unknown';
        const padding = ' '.repeat(Math.max(0, maxKeyLength - entryKey.length + 4));
        result += `${entryKey}${padding}${formatValue(entry.value)};\n`;
      }
    });
    
    result += '\n';
  }
  
  // Format sections (like vertices, blocks, etc.)
  ast.sections.forEach(section => {
    result += formatSection(section);
    result += '\n';
  });
  
  return result;
}

/**
 * Format a value based on its type
 * @param {any} value - The value to format
 * @returns {string} Formatted value
 */
function formatValue(value) {
  if (value === null || value === undefined) {
    return '';
  }
  
  if (typeof value === 'number') {
    return value.toString();
  }
  
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  
  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return formatArray(value);
    }
    
    if (value.type === 'dictionary') {
      return formatDictionary(value);
    }
    
    if (value.type === 'substitution') {
      const variableName = value.variableName || value.value?.replace(/^\$/, '') || 'var';
      return `$${variableName}`;
    }
  }
  
  // Check for substitution string pattern
  if (typeof value === 'string' && value.startsWith('$')) {
    return value;
  }
  
  // Default to string, with quotes if it contains spaces or special chars
  const stringValue = String(value);
  if (/[\s;{}()]/.test(stringValue) || stringValue === '') {
    return `"${stringValue}"`;
  }
  
  return stringValue;
}

/**
 * Format an array value
 * @param {Array} array - The array to format
 * @returns {string} Formatted array
 */
function formatArray(array) {
  if (array.length === 0) {
    return '()';
  }
  
  // For short arrays, format on a single line
  if (array.length <= 3 && array.every(item => typeof item !== 'object')) {
    return `(${array.map(formatValue).join(' ')})`;
  }
  
  // For longer arrays, format with line breaks
  let result = '(\n';
  array.forEach(item => {
    result += `    ${formatValue(item)}\n`;
  });
  result += ')';
  
  return result;
}

/**
 * Format a dictionary value
 * @param {Object} dict - The dictionary to format
 * @returns {string} Formatted dictionary
 */
function formatDictionary(dict) {
  let result = '{\n';
  
  if (dict.entries && dict.entries.length > 0) {
    const maxKeyLength = Math.max(...dict.entries.map(e => e.key?.length || 0));
    
    dict.entries.forEach(entry => {
      // Handle substitution statements within dictionaries
      if (entry.type === 'substitution') {
        const variableName = entry.variableName || entry.value?.replace(/^\$/, '') || 'var';
        result += `    $${variableName};\n`;
      } else {
        const entryKey = entry.key || 'unknown';
        const padding = ' '.repeat(Math.max(0, maxKeyLength - entryKey.length + 4));
        result += `    ${entryKey}${padding}${formatValue(entry.value)};\n`;
      }
    });
  }
  
  if (dict.sections && dict.sections.length > 0) {
    dict.sections.forEach(section => {
      result += `    ${formatSection(section, 4)}\n`;
    });
  }
  
  result += '}';
  return result;
}

/**
 * Format a section (vertices, blocks, etc.)
 * @param {Object} section - The section to format
 * @param {number} baseIndent - Base indentation level
 * @returns {string} Formatted section
 */
function formatSection(section, baseIndent = 0) {
  const indent = ' '.repeat(baseIndent);
  let result = `${indent}${section.kind}\n${indent}(\n`;
  
  // Format tuples
  section.tuples.forEach(tuple => {
    result += `${indent}    `;
    
    if (tuple.items && tuple.items.length > 0) {
      result += tuple.items.map(formatValue).join(' ');
    }
    
    result += '\n';
  });
  
  result += `${indent});\n`;
  return result;
}

/**
 * Pretty print an AST tree structure for debugging
 * @param {Object} ast - The OpenFOAM AST to print
 * @returns {string} Pretty-printed AST structure
 */
export function prettyPrintAst(ast) {
  if (!ast) return 'Empty AST';
  
  // Use JSON.stringify with custom replacer and indentation
  return JSON.stringify(ast, (key, value) => {
    // Skip circular references and functions
    if (typeof value === 'function') {
      return '[Function]';
    }
    return value;
  }, 2);
}

/**
 * Log the AST tree structure to console in a pretty format
 * @param {Object} ast - The OpenFOAM AST to log
 * @param {string} [label='OpenFOAM AST'] - Optional label for the log
 */
export function logAstTree(ast, label = 'OpenFOAM AST') {
  console.group(label);
  
  if (!ast) {
    console.log('Empty AST');
    console.groupEnd();
    return;
  }
  
  // Log entries
  if (ast.entries && ast.entries.length > 0) {
    console.group('Entries');
    ast.entries.forEach((entry, index) => {
      console.group(`Entry ${index + 1}: ${entry.key}`);
      console.log('Value:', typeof entry.value === 'object' 
        ? '(complex value)' 
        : entry.value);
      if (typeof entry.value === 'object' && entry.value !== null) {
        if (entry.value.type === 'dictionary') {
          console.log('Type: Dictionary');
          logAstTree(entry.value, 'Dictionary Value');
        } else if (Array.isArray(entry.value)) {
          console.log('Type: Array');
          console.log('Items:', entry.value);
        } else {
          console.log('Type:', entry.value.type || 'Object');
          console.log('Details:', entry.value);
        }
      }
      console.groupEnd();
    });
    console.groupEnd();
  } else {
    console.log('No entries');
  }
  
  // Log sections
  if (ast.sections && ast.sections.length > 0) {
    console.group('Sections');
    ast.sections.forEach((section, index) => {
      console.group(`Section ${index + 1}: ${section.kind}`);
      console.log('Tuples:', section.tuples.length);
      section.tuples.forEach((tuple, tupleIndex) => {
        console.log(`  Tuple ${tupleIndex + 1}:`, tuple.items);
      });
      console.groupEnd();
    });
    console.groupEnd();
  } else {
    console.log('No sections');
  }
  
  console.groupEnd();
}

/**
 * Format a FoamFile dictionary
 * @param {Object} foamFileEntry - The FoamFile entry
 * @returns {string} Formatted FoamFile dictionary
 */
function formatFoamFile(foamFileEntry) {
  if (!foamFileEntry) return '';
  
  let result = 'FoamFile\n{\n';
  
  // Define the exact sequence of FoamFile entries
  const entrySequence = ['version', 'format', 'class', 'location', 'object'];
  
  // Create a map of actual values
  const entryMap = {};
  if (typeof foamFileEntry.value === 'object' && foamFileEntry.value.entries) {
    foamFileEntry.value.entries.forEach(entry => {
      entryMap[entry.key] = entry.value;
    });
  }
  
  // Find the maximum key length for padding
  const maxKeyLength = Math.max(...entrySequence.map(key => key.length));
  
  // Format entries in the specified sequence
  entrySequence.forEach(key => {
    if (entryMap[key] !== undefined) {
      const padding = ' '.repeat(maxKeyLength - key.length + 4);
      result += `    ${key}${padding}${formatValue(entryMap[key])};\n`;
    }
  });
  
  result += '}\n\n';
  return result;
}

/**
 * Format OpenFOAM dictionary to pretty text
 * @param {Object} ast - The AST to format
 * @returns {string} Formatted OpenFOAM text
 */
export function formatOpenFoam(ast) {
  let result = '';
  
  // Handle FoamFile specially
  const foamFileEntry = ast.entries.find(entry => entry.key === 'FoamFile');
  if (foamFileEntry) {
    result += formatFoamFile(foamFileEntry);
    
    // Remove FoamFile from entries to avoid processing it again
    ast.entries = ast.entries.filter(entry => entry.key !== 'FoamFile');
  }
  
  // Process remaining entries
  if (ast.entries && ast.entries.length > 0) {
    const maxKeyLength = Math.max(...ast.entries.map(e => e.key.length));
    
    ast.entries.forEach(entry => {
      const padding = ' '.repeat(maxKeyLength - entry.key.length + 4);
      result += `${entry.key}${padding}${formatValue(entry.value)};\n`;
    });
  }
  
  return result;
}

